import React, { useState, useCallback } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

// Alignment is looked up rather than interpolated: Tailwind reads these class names out of the
// source text, and a `text-${align}` it never sees literally is a class it never builds.
const ALIGN = {
  left:   { text: 'text-left',   justify: 'justify-start' },
  center: { text: 'text-center', justify: 'justify-center' },
  right:  { text: 'text-right',  justify: 'justify-end' },
};

/**
 * A sortable column header. First click (or Enter / Space) sorts ascending, choosing the same column
 * again turns it around. Both chevrons are drawn on every sortable column so the header advertises that it can be
 * sorted before anyone tries it; the active direction is the emerald one.
 *
 * Padding travels in `className` because the tables in this app are not all on one scale (px-4 py-3
 * in the settings tables, px-6 py-4 in the full-page lists), and `align` mirrors the cells below so
 * a money column keeps its header over the numbers.
 */
const SortHeader = ({ label, sortKey, sortConfig, onSort, align = 'left', className = 'px-4 py-3' }) => {
  const isActive = sortConfig?.key === sortKey;
  const direction = isActive ? sortConfig.direction : null;
  const a = ALIGN[align] || ALIGN.left;
  return (
    <th
      className={`${className} ${a.text} text-xs font-semibold text-gray-500 uppercase tracking-wider select-none`}
      aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}
    >
      {/* The sort is on a button, not on the header cell: a header cell cannot take keyboard focus, so
          the columns could only ever be sorted with a mouse. `uppercase` is repeated because buttons
          reset text-transform. */}
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={`Sort by ${label}`}
        className={`flex w-full items-center gap-1 ${a.justify} ${a.text} uppercase tracking-wider cursor-pointer rounded hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500`}
      >
        {label}
        <span className="flex flex-col" aria-hidden="true">
          <ChevronUp className={`h-3 w-3 -mb-0.5 ${direction === 'asc' ? 'text-emerald-600' : 'text-gray-300'}`} />
          <ChevronDown className={`h-3 w-3 -mt-0.5 ${direction === 'desc' ? 'text-emerald-600' : 'text-gray-300'}`} />
        </span>
      </button>
    </th>
  );
};

/**
 * Sort state for one table. Left unsorted by default so a list arrives in whatever order the
 * server chose — usually newest first, which is the right answer until someone asks otherwise.
 */
export const useSort = (initialKey = null, initialDirection = 'asc') => {
  const [sortConfig, setSortConfig] = useState({ key: initialKey, direction: initialDirection });
  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);
  return { sortConfig, handleSort, setSortConfig };
};

/**
 * Order `rows` by whichever column is active. `accessors` maps a sortKey to the value that column
 * sorts on, so each table states what its columns mean and nothing here has to know about invoices
 * or medicines. An unknown or absent key returns the rows untouched, original order intact.
 */
export const sortRows = (rows, sortConfig, accessors) => {
  const get = sortConfig?.key ? accessors[sortConfig.key] : null;
  if (!get) return rows;
  const dir = sortConfig.direction === 'desc' ? -1 : 1;
  // Sort is stable, so rows the column cannot tell apart stay in the order they arrived in.
  return [...rows].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    const aEmpty = av === null || av === undefined || av === '';
    const bEmpty = bv === null || bv === undefined || bv === '';
    // A blank has no position in the order, so it sits at the bottom whichever way the column
    // points rather than taking over the top half every time it is reversed.
    if (aEmpty || bEmpty) return aEmpty && bEmpty ? 0 : aEmpty ? 1 : -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
    // `numeric` so INV-...-0009 lands before INV-...-0010 instead of after it.
    return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
  });
};

export default SortHeader;
